import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import Fuse from "fuse.js";
import * as Icons from "react-feather";
import { connect } from "react-redux";
import * as Router from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";
import { DashboardLayout } from "component/layout/dashboard";
import { LoadingIndicator } from "component/shared/loading-indicator";
import { PageHeader } from "component/shared/page-header";
import { Office } from "model/office";
import { getOffices, getPages } from "redux/selector";
import { fetchPages } from "redux/slice/pages";
import { RootState } from "redux/store";
import { PageProps } from "shared/page-props";
import { PageType } from "type/page-type";
import { OfficeTable } from "./office-table";
import { styles } from "./style";

interface Props
	extends ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps>,
		PageProps,
		Router.RouteComponentProps,
		WithStyles<typeof styles> {}

interface State {
	term: string;
}

const mapStateToProps = (state: RootState) => {
	const offices = getOffices(state);
	const options: Fuse.IFuseOptions<Office> = {
		shouldSort: true,
		threshold: 0.5,
		location: 0,
		distance: 100,
		minMatchCharLength: 1,
		keys: ["id", "name", "address", "city"],
	};
	return {
		offices,
		fuse: new Fuse(offices, options),
		loading: state.offices.loading,
		pages: getPages(state),
	};
};

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators({
		fetchPages,
	}, dispatch);

class Component extends React.Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		this.state = {
			term: "",
		};
	}

	public componentDidMount() {
		this.props.fetchPages();
	}

	public render() {
		const { loading, pages, user } = this.props;
		let { offices } = this.props;
		const { term } = this.state;
		const title = "Offices";
		if (term) {
			offices = this.props.fuse.search(term).map((result) => result.item);
		}
		// Link to the client's live Offices page (same pattern as settings-locations).
		const officesPage = pages?.find((page) => page.type.id === PageType.OFFICES.id);
		const viewOfficesUrl = officesPage ? user.baseUrl + officesPage.path : undefined;
		return (
			<DashboardLayout
				permitted={user && user.permissions.broker}
				title={title}
				header={
					<PageHeader
						title={title}
						description="Organize by office, assign agents to each location, and generate shortcodes to showcase office listings on your website."
						action={
							viewOfficesUrl && (
								<Mui.Link href={viewOfficesUrl} target="_blank" rel="noopener noreferrer">
									View Offices <Icons.ExternalLink size={14} />
								</Mui.Link>
							)
						}
					/>
				}
			>
				{loading ? (
					<LoadingIndicator />
				) : (
					<OfficeTable
						offices={offices}
						user={user}
						searchTerm={term}
						onSearchChange={(value) => this.setState({ term: value })}
					/>
				)}
			</DashboardLayout>
		);
	}
}

export const SettingsOfficesPage = withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles);
